const AbstractDojoRequestRecord = require('@/lib/PaymentMethods/Dojo/Records/Request/AbstractDojoRequestRecord');
const DojoTransactionNotifications = require('@/lib/PaymentMethods/Dojo/DojoTransactionNotifications');
const DojoTransactionResultStatus = require('@/lib/PaymentMethods/Dojo/DojoTransactionResultStatus');

class DojoTransaction {
    /**
     * DojoTransaction holds all the information about a SALE or REFUND request made to the Dojo API. As the transaction
     * is processed by Dojo we update this object to store the current state.
     *
     * @param {AbstractDojoRequestRecord} request A class that extends AbstractDojoRequestRecord.
     * @param {number} amount
     */
    constructor(request, amount) {
        if (!request || !(request instanceof AbstractDojoRequestRecord)) {
            throw new Error('DojoTransaction requires a class that extends AbstractDojoRequestRecord');
        }

        if (!amount || typeof amount !== 'number') {
            throw new Error('DojoTransaction requires amount to be a number');
        }

        /**
         * @type {AbstractDojoRequestRecord}
         */
        this.initialRequest = request;

        /**
         * @type {number}
         */
        this.amountInMajorUnits = amount;

        /**
         * @type {string|null}
         */
        this.requestId = null;

        /**
         * @type {string|null}
         */
        this.thirdPartyID = null;

        /**
         * Axios Response
         *
         * @see https://axios-http.com/docs/res_schema
         *
         * @type {Object}
         */
        this.latestResponse = null;

        /**
         * An UPPER_SNAKES_CASE string that gives the current status of the transaction e.g. CONNECTING
         * See DojoTransactionNotifications.js
         *
         * @type {?string}
         */
        this.status = null;

        /**
         * The history of statuses returned by Dojo.
         *
         * @type {string[]}
         */
        this.notificationStack = [];

        /**
         * Is the transaction complete?
         *
         * @type {boolean}
         */
        this.isFinished = false;

        /**
         * Are we waiting for the operator to accept or reject the signature (i.e. is the modal open)?
         *
         * @type {boolean}
         */
        this.isWaitingForSignatureCheck = false;

        /**
         * A more detailed message than status that should be shown to the operator.
         *
         * @type {?string}
         */
        this.userMessage = null;

        /**
         * When status === TRANSACTION_FINISHED what is the result?
         * See DojoTransactionResultStatus.js
         *
         * @type {{message: ?string, class: ?string}}
         */
        this.transactionResult = {
            message: null,
            class: null,
        };
    }

    addResponse(response) {
        this.latestResponse = response;

        // There should never be a scenario when we have a 2xx response without data and notifications but check anyway.
        if (response.data && response.data.notifications && response.data.notifications) {
            if (DojoTransactionNotifications.hasOwnProperty(response.data.notifications[0])) {
                this.status = response.data.notifications[0];
                this.notificationStack = response.data.notifications;
            } else {
                throw new Error('Unrecognised Dojo notification in response: ' + response.data.notifications[0]);
            }
        } else {
            throw new Error('Missing Dojo notification in response');
        }

        // Update the userMessage if there is a new userMessage otherwise leave any existing userMessage.
        if (response.data && response.data.userMessage) {
            this.userMessage = response.data.userMessage;
        }

        // The final state of the transaction.
        if (response.data && response.data.transactionResult) {
            if (DojoTransactionResultStatus.hasOwnProperty(response.data.transactionResult)) {
                this.transactionResult.message = response.data.transactionResult;
            } else {
                throw new Error('Unrecognised Dojo transactionResult: ' + response.data.transactionResult);
            }
        }
    }
}

module.exports = DojoTransaction;
